import React, { useEffect, useMemo, useRef, useState } from 'react'
import ConfiguratorIcon from '../../components/ConfiguratorButton/ConfiguratorIcon'
import cx from 'classnames'
import Icon from '../../components/Icon'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import Sidebar from '../Individualization/Progress/DefineAreas/Sidebar'
import styles from './CustomizerPopup.module.css'
import TextElementInput from './ElementInputs/TextElementInput'
import CuttingMaskElementInput from './ElementInputs/CuttingMaskElementInput'
import Loader from '../../components/Loader'
import { useCustomizer } from '../../contexts/CustomizerContext'
import AutoTextElementInput from './ElementInputs/AutoTextElementInput'
import { getTranslatedObject } from '../../helpers/getTranslatedObject'
import {
    StoredSpot,
    StoredTemplate,
    Template,
} from '../../types/Individualization'
import ImgElementInput from './ElementInputs/ImageElementInput'
import Checkout from './Checkout/Checkout'
import {
    resizeEverything,
    changeBackground,
    addCanvasToWrapper,
} from '../../helpers/resizeEverything'
import { usePluginContext } from '../../contexts/PluginContext'

const CustomizerPopup: React.FC = () => {
    const { plugin } = usePluginContext()
    const {
        isLoading,
        activeTemplate,
        activeTemplateId,
        setActiveTemplateId,
        individualization,
        uuids,
        downloadCustomization,
        buttonSetting,
        templates,
    } = useCustomizer()
    const wrapper = useRef<HTMLDivElement | null>(null)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)
    const [showSidebar, setShowSidebar] = useState<boolean>(true)

    useEffect(() => {
        const form = document.getElementById('buy_form')

        if (!form) return

        const preventSubmit = (e: Event) => {
            e.preventDefault()
            console.log('Buy form submission prevented in customizer popup.')
        }

        form.addEventListener('submit', preventSubmit, true)

        return () => {
            form.removeEventListener('submit', preventSubmit, true)
        }
    }, [])

    useEffect(() => {
        if (isLoading || !wrapper.current) return

        resizeObserver.current = new ResizeObserver(() => {
            clearTimeout(timer.current)
            timer.current = setTimeout(() => {
                templates.forEach((template) => {
                    if (template.canvas && wrapper.current) {
                        resizeEverything(template.canvas, wrapper.current, 1000)
                    }
                })
            }, 500)
        })
        resizeObserver.current.observe(wrapper.current)

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect()
                resizeObserver.current = null
            }
            if (timer.current) {
                clearTimeout(timer.current)
                timer.current = undefined
            }
        }
    }, [isLoading, templates])

    useEffect(() => {
        if (isLoading || !wrapper.current) return

        if (activeTemplate && activeTemplate.canvas) {
            addCanvasToWrapper(activeTemplate.canvas, wrapper.current, true)
            resizeEverything(activeTemplate.canvas, wrapper.current, 1000)
            changeBackground(activeTemplate, activeTemplate.canvas)
        }
    }, [activeTemplate, isLoading])

    const translatedTemplates = useMemo(() => {
        if (!plugin?.language) return individualization.templates
        return individualization.templates.map((t) =>
            getTranslatedObject<Template | StoredTemplate>(t, plugin?.language)
        )
    }, [individualization.templates, plugin])

    const translatedSpots = useMemo(() => {
        if (!plugin?.language) return activeTemplate?.spots
        return activeTemplate?.spots.map((t) =>
            getTranslatedObject<StoredSpot>(t, plugin?.language)
        )
    }, [activeTemplate?.spots, plugin])

    return isLoading ? (
        <div
            className={'c-body-wrapper zr-position-absolute'}
            style={{ top: 'calc(50% - 3rem)', left: 'calc(50% - 3rem)' }}
        >
            <Loader isLoading={isLoading} size={'l'} />
        </div>
    ) : (
        <>
            <div className={cx(styles['c-wrapper'])}>
                <div className={cx(styles['c-header-wrapper'])}>
                    <div className={cx(styles['c-header-title'])}>
                        <ConfiguratorIcon
                            fill={buttonSetting?.background_color ?? '#525252'}
                        />
                        <div
                            style={{
                                marginLeft: '0.5rem',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: individualization?.article?.cName,
                            }}
                        />
                    </div>
                </div>

                <div className={cx(styles['c-body-wrapper'])}>
                    <div
                        className={'zr-position-relative'}
                        style={{ overflow: 'hidden' }}
                    >
                        <Sidebar
                            templates={translatedTemplates}
                            active={activeTemplateId}
                            changeTemplate={(id) =>
                                setActiveTemplateId(Number(id))
                            }
                            show={showSidebar}
                            toggleSidebar={() =>
                                setShowSidebar((prev) => !prev)
                            }
                            colors={{
                                background:
                                    buttonSetting?.background_color ?? '#FC0',
                                text: buttonSetting?.text_color ?? '#000',
                            }}
                        />
                        <div
                            ref={wrapper}
                            className={cx(styles['c-canvas-wrapper'])}
                        />
                        <div
                            onClick={() => downloadCustomization()}
                            className={cx(styles['c-save-wrapper'])}
                            style={{
                                background:
                                    buttonSetting?.background_color ?? '#000',
                            }}
                        >
                            <Icon
                                icon={faSave}
                                color={buttonSetting?.text_color ?? '#2d2d2d'}
                                size={'lg'}
                            />
                        </div>
                    </div>
                    <div className={cx(styles['c-right-wrapper'])}>
                        <div className={cx(styles['c-input-wrapper'])}>
                            {!isLoading &&
                                translatedSpots?.map((spot) => {
                                    if (
                                        spot.type === 'text' &&
                                        spot.sync_id === 0
                                    ) {
                                        return (
                                            <TextElementInput
                                                key={
                                                    uuids.get(spot.id) as string
                                                }
                                                spot={spot}
                                                uid={
                                                    uuids.get(spot.id) as string
                                                }
                                            />
                                        )
                                    } else if (
                                        spot.type === 'auto text' &&
                                        spot.sync_id === 0
                                    ) {
                                        return (
                                            <AutoTextElementInput
                                                key={
                                                    uuids.get(spot.id) as string
                                                }
                                                spot={spot}
                                                uid={
                                                    uuids.get(spot.id) as string
                                                }
                                            />
                                        )
                                    } else if (spot.type === 'image') {
                                        return (
                                            <ImgElementInput
                                                key={
                                                    uuids.get(spot.id) as string
                                                }
                                                spot={spot}
                                                uid={
                                                    uuids.get(spot.id) as string
                                                }
                                            />
                                        )
                                    } else if (spot.type === 'cutting mask') {
                                        return (
                                            <CuttingMaskElementInput
                                                key={spot.id}
                                                spot={spot}
                                            />
                                        )
                                    } else return null
                                })}
                        </div>
                        <Checkout />
                    </div>
                </div>
            </div>
        </>
    )
}

export default CustomizerPopup
